import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SetupPassword.js — v6.32.207 (FU-006)
 * Public page reached via the magic link in a super_admin-created user's
 * welcome email. Verifies the one-time setup token, lets the user set their
 * first password, then redirects to /login.
 */
export default function SetupPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');
  const [user, setUser] = useState(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifyError('No setup token in the link. Please use the link from your welcome email.');
      setVerifying(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/setup-password/verify?token=${encodeURIComponent(token)}`);
        setUser(res.data);
      } catch (e) {
        setVerifyError(e.response?.data?.detail || 'This setup link is invalid or has expired.');
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/setup-password`, { token, password });
      toast.success('Password set — please sign in');
      setTimeout(() => navigate('/login'), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="setup-password-loading">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3 text-[#002FA7]" size={32} />
          <p className="text-sm text-gray-500">Verifying your invite link…</p>
        </div>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="setup-password-error">
        <div className="max-w-md w-full border-2 border-red-200 bg-red-50 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={22} />
            <div>
              <h2 className="font-bold text-red-900 mb-1">Invalid or expired link</h2>
              <p className="text-sm text-red-800 mb-3">{verifyError}</p>
              <p className="text-xs text-red-700">
                Please ask the MOSAIC administrator who invited you to resend the invite. The link expires after 7 days.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const roleLabel = (user?.role || '').replace('_', ' ');

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12" data-testid="setup-password-form">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-2 mb-4 text-[#002FA7]">
          <ShieldCheck size={24} />
          <h1 className="text-2xl font-bold">Set your MOSAIC password</h1>
        </div>
        <div className="bg-gray-50 border border-gray-200 p-4 mb-5 text-sm">
          <div><span className="text-gray-500">Name:</span> <span className="font-medium">{user?.name}</span></div>
          <div><span className="text-gray-500">Email:</span> <span className="font-medium">{user?.email}</span></div>
          <div><span className="text-gray-500">Role:</span> <span className="font-medium capitalize">{roleLabel}</span></div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold mb-1 block">New password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="h-10 border-2 border-gray-300 rounded-none"
              autoComplete="new-password"
              required
              data-testid="setup-password-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Confirm password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              className="h-10 border-2 border-gray-300 rounded-none"
              autoComplete="new-password"
              required
              data-testid="setup-password-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="btn-sharp w-full h-11"
            data-testid="setup-password-submit"
          >
            {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Setting password…</> : 'Set password and sign in'}
          </Button>
        </form>
        <p className="text-xs text-gray-500 mt-4 text-center">
          After setting your password you'll be redirected to the sign-in page.
        </p>
      </div>
    </div>
  );
}
